import {
  mapCategoryColorToIndicator,
  resolveCategoryColor
} from "@/helpers/behaviourCoreValueForm.js"

/**
 * Maps API positive behaviour list item to table row shape.
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
export function mapApiPositiveBehaviourToRow(item) {
  const statusNum = Number(item?.status)
  const status = statusNum === 0 ? "inactive" : "active"
  const coreValue = item?.core_value || item?.coreValue || {}
  const coreValueCategory = String(
    coreValue?.category ?? item?.core_value_category ?? ""
  ).trim()
  const coreValueCategoryColor = resolveCategoryColor(coreValueCategory)

  return {
    id: item?.id,
    name: String(item?.name ?? ""),
    awardDescription: String(
      item?.description ??
        item?.award_description ??
        item?.awardDescription ??
        ""
    ),
    points: item?.points_awarded ?? item?.points ?? null,
    coreValueId: item?.core_value_id ?? coreValue?.id ?? null,
    coreValueName: String(coreValue?.name ?? item?.core_value_name ?? ""),
    coreValueCategory,
    coreValueCategoryColor,
    coreValueIndicatorColor: coreValueCategoryColor
      ? mapCategoryColorToIndicator(coreValueCategoryColor)
      : undefined,
    sort_order: item?.sort_order ?? null,
    status
  }
}

/** Allowed values for the positive behaviours list `sort` query param. */
export const POSITIVE_BEHAVIOUR_SORT_FIELDS = Object.freeze([
  "name",
  "core_value_id",
  "award_description",
  "points_awarded",
  "status"
])

/** Allowed values for the positive behaviours list `order` query param. */
export const POSITIVE_BEHAVIOUR_SORT_ORDERS = Object.freeze(["asc", "desc"])

/**
 * Maps a row column key (table field) to the API `sort` value.
 * Returns `null` when the column is not sortable on the server.
 * @param {string|null|undefined} columnKey
 * @returns {string|null}
 */
export function mapPositiveBehaviourSortKey(columnKey) {
  switch (String(columnKey ?? "").trim()) {
    case "name":
      return "name"
    case "coreValueName":
      return "core_value_id"
    case "awardDescription":
      return "award_description"
    case "points":
      return "points_awarded"
    case "status":
      return "status"
    default:
      return null
  }
}

/**
 * @param {{
 *   page?: number,
 *   perPage?: number,
 *   search?: string,
 *   statusFilter?: string,
 *   coreValueId?: string | number | null,
 *   sortBy?: string|null,
 *   sortOrder?: string|null
 * }} args
 * @returns {Record<string, string | number>}
 */
export function buildPositiveBehavioursListParams({
  page = 1,
  perPage = 25,
  search = "",
  statusFilter = "all",
  coreValueId = null,
  sortBy = null,
  sortOrder = null
} = {}) {
  const params = {
    page: Math.max(1, Number(page) || 1),
    per_page: Math.max(1, Number(perPage) || 25)
  }

  const q = String(search ?? "").trim()
  if (q) {
    params.search = q
  }

  if (statusFilter === "active") {
    params.status = 1
  } else if (statusFilter === "inactive") {
    params.status = 0
  }

  const coreValueRaw = String(coreValueId ?? "").trim()
  if (coreValueRaw && coreValueRaw !== "all") {
    const coreValueNumeric = Number(coreValueRaw)
    if (Number.isFinite(coreValueNumeric)) {
      params.core_value_id = coreValueNumeric
    }
  }

  const sortByValue = String(sortBy ?? "").trim()
  const sortOrderValue = String(sortOrder ?? "")
    .trim()
    .toLowerCase()
  if (
    POSITIVE_BEHAVIOUR_SORT_FIELDS.includes(sortByValue) &&
    POSITIVE_BEHAVIOUR_SORT_ORDERS.includes(sortOrderValue)
  ) {
    params.sort = sortByValue
    params.order = sortOrderValue
  }

  return params
}
